import { useState, useRef, useEffect, type ReactNode } from 'react'
import * as ContextMenu from '@radix-ui/react-context-menu'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import {
  FolderOpen,
  FolderPlus,
  Check,
  Play,
  Square,
  RefreshCw,
  Share2,
  CopyPlus,
  Trash2,
  Pencil,
  Globe,
} from 'lucide-react'
import type { App, Folder } from '../../lib/api'
import AppDeleteDialog from '../AppDeleteDialog'
import { FOLDER_COLORS } from './folderColors'

// ---------- Inline folder creation for menus ----------

function InlineContextFolderCreate({ onCreateFolder }: { onCreateFolder: (name: string) => void }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus()
  }, [editing])

  const commit = () => {
    const trimmed = value.trim()
    if (trimmed) onCreateFolder(trimmed)
    setValue('')
    setEditing(false)
  }

  if (editing) {
    return (
      <div className="px-3 py-1.5 flex items-center gap-2">
        <FolderPlus className="h-4 w-4 text-muted-foreground shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation()
            if (e.key === 'Enter') { e.preventDefault(); commit() }
            else if (e.key === 'Escape') { e.preventDefault(); setValue(''); setEditing(false) }
          }}
          onBlur={() => {
            if (value.trim()) commit()
            else { setValue(''); setEditing(false) }
          }}
          placeholder="Folder name"
          className="flex-1 text-sm bg-transparent outline-none min-w-0"
        />
      </div>
    )
  }

  return (
    <ContextMenu.Item
      className="px-3 py-1.5 text-sm hover:bg-secondary cursor-pointer flex items-center gap-2 outline-none"
      onSelect={(e) => {
        e.preventDefault()
        setEditing(true)
      }}
    >
      <FolderPlus className="h-4 w-4" />
      New Folder...
    </ContextMenu.Item>
  )
}

export function InlineDropdownFolderCreate({ onCreateFolder }: { onCreateFolder: (name: string) => void }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus()
  }, [editing])

  const commit = () => {
    const trimmed = value.trim()
    if (trimmed) onCreateFolder(trimmed)
    setValue('')
    setEditing(false)
  }

  if (editing) {
    return (
      <div className="px-3 py-1.5 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <FolderPlus className="h-4 w-4 text-muted-foreground shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation()
            if (e.key === 'Enter') { e.preventDefault(); commit() }
            else if (e.key === 'Escape') { e.preventDefault(); setValue(''); setEditing(false) }
          }}
          onBlur={() => {
            if (value.trim()) commit()
            else { setValue(''); setEditing(false) }
          }}
          placeholder="Folder name"
          className="flex-1 text-sm bg-transparent outline-none min-w-0"
        />
      </div>
    )
  }

  return (
    <DropdownMenu.Item
      className="px-3 py-1.5 text-sm hover:bg-secondary cursor-pointer flex items-center gap-2 outline-none"
      onSelect={(e) => {
        e.preventDefault()
        setEditing(true)
      }}
    >
      <FolderPlus className="h-4 w-4" />
      New Folder...
    </DropdownMenu.Item>
  )
}

// ---------- Context Menu (right-click) ----------

interface AppContextMenuProps {
  children: ReactNode
  app: App
  folders: Folder[]
  appFolderIds?: number[]
  permission?: 'owner' | 'admin' | 'edit' | 'view'
  onMoveToFolder: (folderId: number) => void
  onRemoveFromFolder?: (folderId: number) => void
  onCreateFolderAndMove?: (name: string) => void
  onStart?: () => void
  onStop?: () => void
  onRebuild?: () => void
  onRename?: () => void
  onClone?: () => void
  onShare?: () => void
  onPublish?: () => void
  onDelete?: () => Promise<void>
}

export function AppContextMenu({
  children,
  app,
  folders,
  appFolderIds,
  permission = 'owner',
  onMoveToFolder,
  onRemoveFromFolder,
  onCreateFolderAndMove,
  onStart,
  onStop,
  onRebuild,
  onRename,
  onClone,
  onShare,
  onPublish,
  onDelete,
}: AppContextMenuProps) {
  const canEdit = permission === 'owner' || permission === 'admin' || permission === 'edit'
  const canDelete = permission === 'owner'
  const hasFolders = folders.length > 0
  const itemClass = 'px-3 py-1.5 text-sm hover:bg-secondary cursor-pointer flex items-center gap-2 outline-none'
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [shareCopied, setShareCopied] = useState(false)

  return (
    <>
      <ContextMenu.Root>
        <ContextMenu.Trigger asChild>
          <div className="h-full">{children}</div>
        </ContextMenu.Trigger>
        <ContextMenu.Portal>
          <ContextMenu.Content className="bg-card border border-border rounded-lg shadow-lg py-1 min-w-[180px] z-50">
          {/* Start/Stop */}
          {canEdit && (
            (app.status === 'stopped' || app.status === 'error') ? (
              onStart && (
                <ContextMenu.Item className={`${itemClass} text-green-500`} onSelect={onStart}>
                  <Play className="w-4 h-4" /> Start App
                </ContextMenu.Item>
              )
            ) : (
              onStop && (
                <ContextMenu.Item className={itemClass} onSelect={onStop}>
                  <Square className="w-4 h-4" /> Stop App
                </ContextMenu.Item>
              )
            )
          )}

          {/* Rebuild */}
          {canEdit && onRebuild && (
            <ContextMenu.Item className={`${itemClass} text-orange-500`} onSelect={onRebuild}>
              <RefreshCw className="w-4 h-4" /> Rebuild
            </ContextMenu.Item>
          )}

          {/* Publish */}
          {canEdit && onPublish && (
            <ContextMenu.Item className={`${itemClass} text-purple-500`} onSelect={onPublish}>
              <Globe className="w-4 h-4" /> Publish
            </ContextMenu.Item>
          )}

          <ContextMenu.Separator className="h-px bg-border my-1" />

          {/* Edit Info */}
          {canEdit && onRename && (
            <ContextMenu.Item className={itemClass} onSelect={onRename}>
              <Pencil className="w-4 h-4" /> Edit Info
            </ContextMenu.Item>
          )}

          {/* Clone */}
          {canEdit && onClone && (
            <ContextMenu.Item className={itemClass} onSelect={onClone}>
              <CopyPlus className="w-4 h-4" /> Clone
            </ContextMenu.Item>
          )}

          {/* Share (all permission levels) */}
          <ContextMenu.Item
            className={itemClass}
            onSelect={(e) => {
              if (canEdit && onShare) {
                onShare()
              } else {
                e.preventDefault() // Keep menu open to show feedback
                navigator.clipboard.writeText(app.url)
                setShareCopied(true)
                setTimeout(() => setShareCopied(false), 1500)
              }
            }}
          >
            <Share2 className="w-4 h-4" /> {shareCopied ? 'URL Copied!' : 'Share'}
          </ContextMenu.Item>

          {/* Move to folder submenu with checkmarks */}
          {(hasFolders || onCreateFolderAndMove) && (
            <ContextMenu.Sub>
              <ContextMenu.SubTrigger className={`${itemClass} data-[state=open]:bg-secondary`}>
                <FolderOpen className="h-4 w-4" />
                Move to Folder
              </ContextMenu.SubTrigger>
              <ContextMenu.Portal>
                <ContextMenu.SubContent className="bg-card border border-border rounded-lg shadow-lg py-1 min-w-[160px] z-50 max-h-[240px] overflow-y-auto">
                  {onCreateFolderAndMove && (
                    <>
                      <InlineContextFolderCreate onCreateFolder={onCreateFolderAndMove} />
                      {hasFolders && <ContextMenu.Separator className="h-px bg-border my-1" />}
                    </>
                  )}
                  {folders.map((folder) => {
                    const colorSet = FOLDER_COLORS[folder.color] || FOLDER_COLORS.gray
                    const isInFolder = appFolderIds?.includes(folder.id)
                    return (
                      <ContextMenu.CheckboxItem
                        key={folder.id}
                        checked={isInFolder}
                        className="px-3 py-1.5 text-sm hover:bg-secondary cursor-pointer flex items-center gap-2 outline-none"
                        onSelect={(e) => {
                          e.preventDefault()
                          if (isInFolder) {
                            onRemoveFromFolder?.(folder.id)
                          } else {
                            onMoveToFolder(folder.id)
                          }
                        }}
                      >
                        <ContextMenu.ItemIndicator className="w-4 h-4 flex items-center justify-center shrink-0">
                          <Check className="w-3.5 h-3.5" />
                        </ContextMenu.ItemIndicator>
                        <span className={`w-2.5 h-2.5 rounded-full ${colorSet.dot} shrink-0 ${isInFolder ? '' : 'ml-4'}`} />
                        {folder.name}
                      </ContextMenu.CheckboxItem>
                    )
                  })}
                </ContextMenu.SubContent>
              </ContextMenu.Portal>
            </ContextMenu.Sub>
          )}

          {/* Delete */}
          {canDelete && onDelete && (
            <>
              <ContextMenu.Separator className="h-px bg-border my-1" />
              <ContextMenu.Item
                className={`${itemClass} text-destructive`}
                onSelect={() => setDeleteDialogOpen(true)}
              >
                <Trash2 className="w-4 h-4" /> Delete
              </ContextMenu.Item>
            </>
          )}
          </ContextMenu.Content>
        </ContextMenu.Portal>
      </ContextMenu.Root>

      {canDelete && onDelete && (
        <AppDeleteDialog
          app={app}
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onDelete={onDelete}
        />
      )}
    </>
  )
}

// ---------- Dropdown Menu Items (for three-dot menus) ----------

interface MoveToFolderMenuItemsProps {
  folders: Folder[]
  appFolderIds?: number[]
  onMoveToFolder: (folderId: number) => void
  onRemoveFromFolder?: (folderId: number) => void
  onCreateFolderAndMove?: (name: string) => void
}

export function MoveToFolderMenuItems({
  folders,
  appFolderIds,
  onMoveToFolder,
  onRemoveFromFolder,
  onCreateFolderAndMove,
}: MoveToFolderMenuItemsProps) {
  const hasFolders = folders.length > 0
  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger className="px-3 py-1.5 text-sm hover:bg-secondary cursor-pointer flex items-center gap-2 outline-none data-[state=open]:bg-secondary">
        <FolderOpen className="h-4 w-4" />
        Move to Folder
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent className="bg-card border border-border rounded-lg shadow-lg py-1 min-w-[160px] z-50 max-h-[240px] overflow-y-auto">
          {onCreateFolderAndMove && (
            <>
              <InlineDropdownFolderCreate onCreateFolder={onCreateFolderAndMove} />
              {hasFolders && <DropdownMenu.Separator className="h-px bg-border my-1" />}
            </>
          )}
          {folders.map((folder) => {
            const colorSet = FOLDER_COLORS[folder.color] || FOLDER_COLORS.gray
            const isInFolder = appFolderIds?.includes(folder.id)
            return (
              <DropdownMenu.CheckboxItem
                key={folder.id}
                checked={isInFolder}
                className="px-3 py-1.5 text-sm hover:bg-secondary cursor-pointer flex items-center gap-2 outline-none"
                onSelect={(e) => {
                  e.preventDefault()
                  if (isInFolder) {
                    onRemoveFromFolder?.(folder.id)
                  } else {
                    onMoveToFolder(folder.id)
                  }
                }}
              >
                <DropdownMenu.ItemIndicator className="w-4 h-4 flex items-center justify-center shrink-0">
                  <Check className="w-3.5 h-3.5" />
                </DropdownMenu.ItemIndicator>
                <span className={`w-2.5 h-2.5 rounded-full ${colorSet.dot} shrink-0 ${isInFolder ? '' : 'ml-4'}`} />
                {folder.name}
              </DropdownMenu.CheckboxItem>
            )
          })}
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  )
}
